import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_BILLING_PORTAL_SESSION } from '@shared/graphql/mutations/payment';
import analyticsService from '../../services/analytics';

// Collapsible section on the subscriptions page that opens the Stripe Customer
// Portal (billing-details-only configuration) for payment method and billing
// address updates. Cancellation and plan changes stay in-app.
const BillingDetailsSection: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [createBillingPortalSession, { loading }] = useMutation(CREATE_BILLING_PORTAL_SESSION, {
    onCompleted: (data) => {
      window.location.href = data.createBillingPortalSession.url;
    },
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to open billing management. Please try again.');
    }
  });

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    try {
      analyticsService.trackEvent('subscription_section_toggled', {
        metadata: {
          section: 'billing_details',
          expanded: !isExpanded,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  };

  const handleManageBilling = async () => {
    setErrorMessage('');
    try {
      analyticsService.trackEvent('billing_portal_opened', {
        metadata: {
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
    await createBillingPortalSession();
  };

  return (
    <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
      <button
        onClick={handleToggle}
        className="w-full flex justify-between items-center text-left"
      >
        <div>
          <h2 className="text-lg font-semibold">Billing Details</h2>
        </div>
        <svg
          className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            Update your payment method and billing address, or view your invoice history,
            through our secure billing portal.
          </p>

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          <button
            onClick={handleManageBilling}
            disabled={loading}
            className="bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Opening billing portal…' : 'Manage Billing Details'}
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingDetailsSection;
